import React, { useEffect, useState } from 'react';
import AdminGuard from '../components/AdminGuard';
import AdminLayout from '../components/AdminLayout';
import StatCard from '../components/StatCard';
import { api } from '../../../api/client';

interface UserStats {
  totalUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  authMethodBreakdown?: {
    google?: number;
    apple?: number;
    email?: number;
  };
}

interface TimelineStats {
  totalTimelines: number;
  publicTimelines: number;
}

interface NotificationStats {
  totalNotifications: number;
  todayNotifications: number;
  successRate: number;
  failedNotifications: number;
  byType?: {
    alert?: number;
    transition?: number;
    liveactivity_update?: number;
    liveactivity_end?: number;
  };
}

interface SystemHealth {
  status: string;
  database: string;
}

const AdminDashboardPage: React.FC = () => {
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [timelineStats, setTimelineStats] = useState<TimelineStats | null>(null);
  const [notificationStats, setNotificationStats] = useState<NotificationStats | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [users, timelines, notifications, health] = await Promise.all([
          api.get('/admin/users/stats') as Promise<UserStats>,
          api.get('/admin/timelines/stats') as Promise<TimelineStats>,
          api.get('/admin/notifications/stats') as Promise<NotificationStats>,
          api.get('/admin/system/health') as Promise<SystemHealth>,
        ]);
        setUserStats(users);
        setTimelineStats(timelines);
        setNotificationStats(notifications);
        setSystemHealth(health);
      } catch (err) {
        const apiError = err as { message?: string };
        setError(apiError.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <AdminGuard>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading...</div>
          </div>
        </AdminLayout>
      </AdminGuard>
    );
  }

  if (error) {
    return (
      <AdminGuard>
        <AdminLayout>
          <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg">
            Error loading dashboard: {error}
          </div>
        </AdminLayout>
      </AdminGuard>
    );
  }

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h2>

          {/* System Status */}
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${systemHealth?.status === 'healthy' ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              System {systemHealth?.status || 'unknown'} - Database {systemHealth?.database || 'unknown'}
            </span>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Users"
              value={userStats?.totalUsers || 0}
              subtitle={`${userStats?.newUsersToday || 0} today, ${userStats?.newUsersThisWeek || 0} this week`}
              linkTo="/admin/users"
            />
            <StatCard
              title="Total Timelines"
              value={timelineStats?.totalTimelines || 0}
              subtitle={`${timelineStats?.publicTimelines || 0} public`}
              linkTo="/admin/timelines"
            />
            <StatCard
              title="Notifications Sent"
              value={notificationStats?.totalNotifications || 0}
              subtitle={`${notificationStats?.todayNotifications || 0} today`}
              linkTo="/admin/notifications"
            />
            <StatCard
              title="Success Rate"
              value={`${((notificationStats?.successRate || 0) * 100).toFixed(1)}%`}
              subtitle={`${notificationStats?.failedNotifications || 0} failed`}
              linkTo="/admin/notifications"
              linkText="View logs"
            />
          </div>

          {/* Auth Method Breakdown */}
          {userStats?.authMethodBreakdown && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Authentication Methods</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {userStats.authMethodBreakdown.google || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Google</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {userStats.authMethodBreakdown.apple || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Apple</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {userStats.authMethodBreakdown.email || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                </div>
              </div>
            </div>
          )}

          {/* Notification Types */}
          {notificationStats?.byType && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Notification Types</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {notificationStats.byType.alert || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Alerts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {notificationStats.byType.transition || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Transitions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {notificationStats.byType.liveactivity_update || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">LA Updates</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {notificationStats.byType.liveactivity_end || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">LA Ends</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
};

export default AdminDashboardPage;
